import { useEffect, useMemo, useState } from "react";
import syrupLogo from "./syrup-logo.svg";
import { T, fontDisplay, fontMono } from "./theme";
import { fmtDay, fmtMoney, isoDate } from "./engine/dates";
import { BulkPanel, CashFlowGrid, DocumentDrawer, Legend, buildForecast, bucketWeeks, headlineFigures } from "./App";

/**
 * The investor view.
 *
 * One page, one number that matters, and a way to ask about it. Everything
 * arrives already computed from the server, so there is no database key in the
 * browser and nothing to poke at — investors see the position, not the book.
 *
 * Built narrow-first: this will mostly be read on a phone, on the move.
 */
export default function InvestorView() {
  const [password, setPassword] = useState(() => sessionStorage.getItem("inv_pw") || "");
  const [data, setData] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem("inv_name") || "");
  const [question, setQuestion] = useState("");
  const [picked, setPicked] = useState(null);
  const [focused, setFocused] = useState(null);
  const [sent, setSent] = useState(false);

  const load = async (pw) => {
    setState("loading");
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/investor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Could not load");
      sessionStorage.setItem("inv_pw", pw);
      setData(json);
      setState("ready");
    } catch (e) {
      sessionStorage.removeItem("inv_pw");
      setError(e.message || String(e));
      setState("idle");
    }
  };

  useEffect(() => {
    if (password) load(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ask = async () => {
    if (!question.trim()) return;
    setSent(false);
    try {
      const res = await fetch("/.netlify/functions/investor", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, action: "comment", name, message: question }),
      });
      if (!res.ok) throw new Error("Could not send");
      localStorage.setItem("inv_name", name);
      setQuestion("");
      setSent(true);
      load(password);
    } catch (e) {
      setError(e.message || String(e));
    }
  };

  // The same forecast the team sees, built in the browser by the same engine
  // from the same snapshot. Investors are shown the grid, not a summary of it.
  const forecast = useMemo(() => buildForecast(data?.data, data?.openingDate), [data]);
  const weeks = useMemo(
    () => bucketWeeks(forecast, data?.openingBalance, data?.openingDate),
    [forecast, data]
  );
  const headline = useMemo(() => headlineFigures(data?.data, forecast), [data, forecast]);

  const summary = useMemo(() => {
    if (!weeks.length) return null;
    const future = weeks.filter((w) => !w.isHistory);
    const now = future[0];
    // Thirteen weeks, not the whole horizon: beyond a quarter the only income
    // loaded is contracted retainers, so the line falls away by construction
    // and the far end measures missing data rather than the business.
    const lowOf = (ws) => ws.reduce((m, w) => (m == null || w.endBal < m.endBal ? w : m), null);
    const highOf = (ws) => ws.reduce((m, w) => (m == null || w.endBal > m.endBal ? w : m), null);
    const near = future.slice(0, 13);
    const month = future.slice(0, 4);
    return {
      now,
      low: lowOf(near),
      high: highOf(near),
      lowMonth: lowOf(month),
      highMonth: highOf(month),
      weeks: future.length,
    };
  }, [weeks]);

  if (state !== "ready") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily: "'Manrope', system-ui, sans-serif",
        }}
      >
        <div style={{ width: "min(360px, 100%)", textAlign: "center" }}>
          <img src={syrupLogo} alt="Syrup" style={{ height: 46, marginBottom: 22 }} />
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>Cash flow · investor view</div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(password)}
            placeholder="Access code"
            style={{
              width: "100%",
              fontSize: 16, // 16px keeps iOS from zooming the page on focus
              padding: "12px 14px",
              borderRadius: 8,
              border: `1px solid ${T.border}`,
              textAlign: "center",
            }}
          />
          <button
            onClick={() => load(password)}
            disabled={state === "loading" || !password}
            style={{
              width: "100%",
              marginTop: 10,
              padding: "12px 14px",
              borderRadius: 8,
              border: "none",
              background: password ? T.accent : T.border,
              color: password ? "#fff" : T.muted,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {state === "loading" ? "Checking…" : "View"}
          </button>
          {error && <div style={{ color: T.cost, fontSize: 12, marginTop: 12 }}>{error}</div>}
        </div>
      </div>
    );
  }

  const card = {
    background: T.panel,
    borderRadius: 12,
    padding: "16px 18px",
    flex: "1 1 200px",
    minWidth: 0,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        fontFamily: "'Manrope', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <div style={{ padding: "18px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <img src={syrupLogo} alt="Syrup" style={{ height: 34 }} />
        <div style={{ fontSize: 12, color: T.muted }}>Cash flow · investor view</div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: T.muted }}>
          as at {fmtDay(data.generatedAt)}
        </div>
      </div>

      <div style={{ padding: "0 16px 24px" }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={card}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Cash this week
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 30,
                color: summary.now?.endBal < 0 ? T.cost : T.ink,
              }}
            >
              {fmtMoney(summary.now?.endBal ?? 0)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>w/e {summary.now ? fmtDay(isoDate(summary.now.end)) : ""}</div>
          </div>
          <div style={card}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Next 30 days
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 28,
                color: summary.lowMonth?.endBal < 0 ? T.cost : T.ink,
              }}
            >
              {fmtMoney(summary.lowMonth?.endBal ?? 0)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              lowest · {fmtMoney(summary.highMonth?.endBal ?? 0)} at best
            </div>
          </div>
          <div style={card}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Lowest · next 13 weeks
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 30,
                color: summary.low?.endBal < 0 ? T.cost : T.ink,
              }}
            >
              {fmtMoney(summary.low?.endBal ?? 0)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>w/e {summary.low ? fmtDay(isoDate(summary.low.end)) : ""}</div>
          </div>
          <div style={card}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Highest · next 13 weeks
            </div>
            <div style={{ fontFamily: fontDisplay, fontSize: 30, color: summary.high?.endBal < 0 ? T.cost : T.ink }}>
              {fmtMoney(summary.high?.endBal ?? 0)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              w/e {summary.high ? fmtDay(isoDate(summary.high.end)) : ""}
            </div>
          </div>
          <div style={card}>
            <div style={{ fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", color: T.muted }}>
              Owed to us
            </div>
            <div style={{ fontFamily: fontDisplay, fontSize: 30, color: T.income }}>
              {fmtMoney(headline.owed)}
            </div>
            <div style={{ fontSize: 11, color: T.muted }}>
              {headline.owedOverdue ? `${fmtMoney(headline.owedOverdue)} of it overdue` : "none overdue"}
            </div>
          </div>
        </div>

        {/* An investor reading a falling line needs to know what is in it. The
            near weeks carry real invoices and bills; the later ones carry only
            contracted retainers against a full cost base, because project work
            not yet won cannot honestly be forecast as cash. */}
        <div
          style={{
            background: T.panel,
            borderRadius: 12,
            padding: "12px 16px",
            marginBottom: 14,
            fontSize: 12,
            lineHeight: 1.6,
            color: T.muted,
          }}
        >
          <strong style={{ color: T.ink }}>What this includes.</strong> Invoices raised and bills
          due, the AMEX balance and its limit, agreed payment plans, HMRC, and contracted retainers
          — each figure traceable to a document. Running costs are the actual monthly cash figures
          from the accounts.
          <br />
          <strong style={{ color: T.ink }}>What it does not.</strong> Project income that has not yet
          been won. The first few weeks are close to fully committed on both sides; further out, the
          costs are complete but the income is only what is already contracted, so the later weeks
          understate where the position is expected to land.
        </div>

        <Legend />

        {picked && picked.length > 1 && !focused && (
          <BulkPanel items={picked} onClose={() => setPicked(null)} onOpenOne={(t) => setFocused(t)} readOnly />
        )}
        {(focused || picked?.length === 1) && (() => {
          const one = focused || picked[0];
          const cameFromList = !!focused && picked && picked.length > 1;
          return (
            <DocumentDrawer
              txn={one}
              updates={[]}
              spend={(forecast?.spendByDoc || {})[`${one.source?.type}:${one.source?.id}`] || []}
              readOnly
              onBack={cameFromList ? () => setFocused(null) : undefined}
              backCount={cameFromList ? picked.length : undefined}
              onClose={() => {
                setFocused(null);
                setPicked(null);
              }}
            />
          );
        })()}

        {/* The same grid the team works from. Read-only: cells do not open, and
            there is no route from this page back to the tables. */}
        <div style={{ overflowX: "auto" }}>
          <CashFlowGrid
            weeks={weeks}
            spendByDoc={forecast?.spendByDoc || {}}
            overdraftLimit={Number(data.overdraftLimit) || 0}
            openingDate={data.openingDate}
            onPick={(txns) => setPicked(txns)}
          />
        </div>


        <div style={{ background: T.panel, borderRadius: 12, padding: 16, marginTop: 20 }}>
          <h2 style={{ fontFamily: fontDisplay, fontSize: 18, margin: "0 0 4px" }}>Questions</h2>
          <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, marginTop: 0 }}>
            Ask anything about the numbers above and Oliver will answer here.
          </p>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            style={{ fontSize: 16, padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.border}`, width: "100%", marginBottom: 8 }}
          />
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder="What's driving the dip in October?"
            style={{ fontSize: 16, padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.border}`, width: "100%", fontFamily: "inherit", resize: "vertical" }}
          />
          <button
            onClick={ask}
            disabled={!question.trim()}
            style={{
              marginTop: 8,
              padding: "11px 18px",
              borderRadius: 8,
              border: "none",
              background: question.trim() ? T.accent : T.border,
              color: question.trim() ? "#fff" : T.muted,
              fontSize: 14,
              cursor: "pointer",
              width: "100%",
              maxWidth: 220,
            }}
          >
            Send question
          </button>
          {sent && <div style={{ fontSize: 12, color: T.income, marginTop: 8 }}>Sent — thank you.</div>}

          {data.comments?.length > 0 && (
            <div style={{ marginTop: 18 }}>
              {data.comments.map((c) => (
                <div key={c.id} style={{ borderTop: `1px solid ${T.borderSoft}`, padding: "10px 0" }}>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{c.message}</div>
                  <div style={{ fontSize: 10, color: T.muted, marginTop: 3 }}>
                    {c.name || "Anonymous"} · {fmtDay(c.createdAt)}
                  </div>
                  {c.answer && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: "8px 10px",
                        background: T.panelSoft,
                        borderRadius: 8,
                        fontSize: 13,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <strong style={{ fontSize: 10, color: T.accent }}>OLIVER</strong>
                      <div>{c.answer}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ fontSize: 10, color: T.muted, textAlign: "center", margin: "24px 0" }}>
          Confidential · prepared by Syrup
        </div>
      </div>
    </div>
  );
}
